import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "@aws-amplify/auth";
import MainLayout from "../../../components/layouts/MainLayout/MainLayout";
import env from "../../../config/env";

const DEFAULT_WALL_BASE = "https://nbv4ejg6u4.execute-api.us-east-1.amazonaws.com/Prod/wall";
const getWallBaseUrl = () => (env.WALL_API_URL || DEFAULT_WALL_BASE).replace(/\/$/, "");

async function getAuthHeader() {
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idToken = tokens?.idToken?.toString();
    if (idToken) return { Authorization: `Bearer ${idToken}` };
  } catch (error) {
    console.error("Alerts.getAuthHeader: unable to fetch session", error);
  }
  return {};
}

async function fetchWithAuth(url, init = {}) {
  const authHeader = await getAuthHeader();
  const headers = {
    ...(init.body ? { "Content-Type": "application/json" } : {}),
    ...(env.API_KEY ? { "x-api-key": env.API_KEY } : {}),
    ...authHeader,
    ...(init.headers || {}),
  };
  const res = await fetch(url, { ...init, headers, mode: "cors", cache: "no-cache" });
  if (res.status === 204) return null;
  const contentType = res.headers.get("content-type") || "";
  const data = contentType.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

const typeBadge = (type) => {
  const map = {
    POST_CREATED: { color: "bg-indigo-50 text-indigo-700", label: "Post" },
    REACTION_ADMIRE: { color: "bg-emerald-50 text-emerald-700", label: "Reacción" },
    COMMENT_CREATED: { color: "bg-blue-50 text-blue-700", label: "Comentario" },
    DAILY_SUPPORT_REQUEST: { color: "bg-amber-50 text-amber-700", label: "Solicitud de apoyo" },
  };
  return map[type] || { color: "bg-gray-100 text-gray-700", label: "Actividad" };
};

const formatRelative = (iso) => {
  if (!iso) return "";
  const now = new Date();
  const then = new Date(iso);
  const diff = now - then;
  const min = Math.floor(diff / (1000 * 60));
  if (min < 60) return `Hecho hace ${min} min`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `Hecho hace ${hrs} hrs`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `Hecho hace ${days} días`;
  const months = Math.floor(days / 30);
  return `Hecho hace ${months} mes${months > 1 ? "es" : ""}`;
};

const Alerts = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [avatarUrlByKey, setAvatarUrlByKey] = useState({});
  const [actorProfileById, setActorProfileById] = useState({}); // { [userId]: { user_name, avatar_key, avatar_url } }
  const [menuOpenKey, setMenuOpenKey] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);

  const events = useMemo(() => items, [items]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const out = await fetchWithAuth(`${getWallBaseUrl()}/notifications?limit=50`, { method: "GET" });
        const list = Array.isArray(out?.items) ? out.items : [];
        if (mounted) setItems(list);
      } catch (e) {
        console.error("Alerts.load", e);
        if (mounted) setError("No se pudieron cargar las alertas. Intenta de nuevo.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Completar nombres/avatares desde el perfil (derivado de Cognito) cuando el evento no los trae.
  useEffect(() => {
    let mounted = true;
    (async () => {
      const missingActorIds = Array.from(
        new Set(
          (items || [])
            .map((n) => n?.actorId)
            .filter(Boolean)
            .filter((uid) => {
              const nHasName = (items || []).some((x) => x?.actorId === uid && (x?.actorName || x?.actor_name));
              return !nHasName && !actorProfileById[uid];
            })
        )
      ).slice(0, 25);

      if (!missingActorIds.length) return;

      try {
        const pairs = await Promise.all(
          missingActorIds.map(async (uid) => {
            try {
              const p = await fetchWithAuth(`${getWallBaseUrl()}/users/${encodeURIComponent(uid)}/profile`, {
                method: "GET",
              });
              return [uid, p];
            } catch {
              return [uid, null];
            }
          })
        );
        if (!mounted) return;
        setActorProfileById((prev) => {
          const next = { ...prev };
          for (const [uid, p] of pairs) {
            if (p) next[uid] = p;
          }
          return next;
        });
      } catch (e) {
        console.error("Alerts.resolveActorProfiles", e);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!menuOpenKey) return;
      if (e.target?.closest?.('[data-alerts-menu="true"]')) return;
      setMenuOpenKey(null);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpenKey]);

  const handleDeleteNotification = async (ev) => {
    const sort = ev?.sort;
    if (!sort) return;
    try {
      setDeletingKey(sort);
      await fetchWithAuth(`${getWallBaseUrl()}/notifications`, {
        method: "DELETE",
        body: JSON.stringify({ sortKeys: [sort] }),
      });
      setItems((prev) => (prev || []).filter((n) => n?.sort !== sort));
      setMenuOpenKey(null);
    } catch (e) {
      console.error("Alerts.delete", e);
      setError("No se pudo borrar la notificación. Intenta de nuevo.");
    } finally {
      setDeletingKey(null);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const keys = Array.from(
        new Set(
          (items || [])
            .map((n) => n?.actorAvatarKey)
            .filter(Boolean)
        )
      );
      if (!keys.length) return;
      try {
        const pairs = await Promise.all(
          keys.map(async (key) => {
            try {
              const out = await fetchWithAuth(`${getWallBaseUrl()}/media?key=${encodeURIComponent(key)}`, {
                method: "GET",
              });
              return [key, out?.url || ""];
            } catch {
              return [key, ""];
            }
          })
        );
        if (!mounted) return;
        setAvatarUrlByKey((prev) => {
          const next = { ...prev };
          for (const [k, url] of pairs) {
            if (url) next[k] = url;
          }
          return next;
        });
      } catch (e) {
        console.error("Alerts.resolveAvatars", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [items]);

  return (
    <MainLayout>
      <div className="flex flex-col" style={{ minHeight: "calc(100vh - 74px)" }}>
        <main className="flex-1 p-6 md:p-8 bg-gray-50 space-y-6 overflow-y-auto">
          <h1 className="text-2xl font-bold">Alertas</h1>
          <p className="text-sm text-gray-600">Avisos de tu red de apoyo: nuevos posts, reacciones, comentarios y solicitudes.</p>

          <div className="grid gap-3">
            {error && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}
            {loading && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
                Cargando alertas…
              </div>
            )}
            {events.map((ev) => {
              const badge = typeBadge(ev.type);
              const prof = ev.actorId ? actorProfileById[ev.actorId] : null;
              const actorName =
                ev.actorName ||
                ev.actor_name ||
                prof?.user_name ||
                prof?.name ||
                prof?.email ||
                ev.actorId ||
                "Usuario";
              const avatarSrc =
                ev.actorAvatarUrl ||
                (ev.actorAvatarKey ? avatarUrlByKey[ev.actorAvatarKey] : "") ||
                (prof?.avatar_key ? avatarUrlByKey[prof.avatar_key] : "") ||
                prof?.avatar_url ||
                "";
              return (
                <div
                  key={ev.sort || ev.notificationId || ev.createdAt}
                  className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-3 shadow-sm"
                >
                  <div
                    className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-lg font-semibold overflow-hidden border border-emerald-50 cursor-pointer"
                    onClick={() => navigate(`/support/user/${ev.actorId || ev.userId}`)}
                  >
                    {avatarSrc ? (
                      <img
                        src={avatarSrc}
                        alt={actorName}
                        className="w-full h-full object-cover"
                        onError={async () => {
                          // Si la URL firmada expiró, intenta re-generarla una vez desde el key.
                          const key = ev.actorAvatarKey || prof?.avatar_key;
                          if (!key) return;
                          try {
                            const out = await fetchWithAuth(
                              `${getWallBaseUrl()}/media?key=${encodeURIComponent(key)}`,
                              { method: "GET" }
                            );
                            const url = out?.url;
                            if (!url) return;
                            setAvatarUrlByKey((prev) => ({ ...prev, [key]: url }));
                          } catch {
                            // ignore
                          }
                        }}
                      />
                    ) : (
                      (actorName || "U").slice(0, 1).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900">{actorName}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${badge.color}`}>{badge.label}</span>
                    </div>
                    <p className="text-sm text-gray-700 truncate">{ev.summary || "Actividad"}</p>
                    <p className="text-xs text-gray-500">
                      {ev.createdAt ? new Date(ev.createdAt).toLocaleString() : ""} ({formatRelative(ev.createdAt)})
                    </p>
                  </div>

                  <div className="relative" data-alerts-menu="true">
                    <button
                      type="button"
                      className="w-9 h-9 rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 flex items-center justify-center"
                      onClick={() => setMenuOpenKey((prev) => (prev === ev.sort ? null : ev.sort))}
                      aria-label="Opciones"
                      title="Opciones"
                    >
                      <span className="text-xl leading-none">⋯</span>
                    </button>
                    {menuOpenKey === ev.sort && (
                      <div className="absolute right-0 mt-2 w-48 rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden z-20">
                        <button
                          type="button"
                          className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                          onClick={() => handleDeleteNotification(ev)}
                          disabled={deletingKey === ev.sort}
                        >
                          {deletingKey === ev.sort ? "Borrando…" : "Borrar notificación"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {!loading && !events.length && !error && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-sm text-gray-600">
                Aún no tienes alertas. Cuando tu red de apoyo interactúe, verás los avisos aquí.
              </div>
            )}
          </div>
        </main>
      </div>
    </MainLayout>
  );
};

export default Alerts;
